import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Inject,
  HostListener,
  ViewChild,
  ElementRef
} from '@angular/core';
import { InterpolateService } from '../../../services/interpolate.service';
import { Observable } from 'rxjs/Observable';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { Store } from '@ngrx/store';
import { ConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/Subject';
import {OrganizationService} from "@ispm/migration/services/organization.service";

@Component({
  selector: 'gen-suggestions-template',
  templateUrl: './suggestions-template.component.html',
  styleUrls: ['./suggestions-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SuggestionsTemplateComponent implements OnInit {

  constructor(private orgService: OrganizationService,
              private interpolate: InterpolateService,
              private store: Store<IgenomicsState>,
              private translateService: TranslateService) {
  }
  @Input() mainValue: any;
  @Input() object: any;
  @Input() multiple: boolean = true;
  @Input() fieldsToDisplay: Array<(string | { display: string, className?: string, source?: string, path?: string })>;
  @Input() fieldTemplate: string;
  @Input() translate: boolean;
  public displayValue: Observable<Array<{ display: string, className?: string, source?: string, path?: string }> | string>;
  public image: string;
  public tooltip: string;
  private destroyed$: Subject<any> = new Subject();

  @ViewChild('tooltipElement') tooltipElement: ElementRef;

  @HostListener('mouseup', ['$event'])
  stopMouseUpEvent($event: Event) {
    if (!this.multiple) {
      return;
    }

    $event.stopPropagation();
  }

  ngOnInit() {
    this.fieldTemplate = this.fieldTemplate.toLowerCase();
    this.setDisplayValue();
  }

  showTooltip() {
    const element = this.tooltipElement.nativeElement;
    this.tooltip = '';

    if (element.scrollWidth > element.clientWidth) {
      if (this.fieldTemplate.trim() === '') {
        this.tooltip = this.mainValue;

        if (this.translate) {
          this.tooltip = this.translateService.instant(this.tooltip);
        }
      }

      this.displayValue
        .takeUntil(this.destroyed$)
        .subscribe(data => {
          switch (this.fieldTemplate) {
            case 'additional-parenthesis':
            case 'twolines':
              if (data['display']) {
                this.tooltip += '(' + data['display'] + ')';
              }
              break;
            default:
              if (Array.isArray(data)) {
                data.forEach(item => {
                  this.tooltip += ' ' + item.display;
                });
              }
          }

          if (this.translate) {
            this.tooltip = this.translateService.instant(this.tooltip);
          }
      });
    }
  }

  private setDisplayValue() {
    const value = this.object;
    const displayValues = this.fieldsToDisplay.map(field => {
      const tempObj = { display: '', className: '', pattern: ''};
      let stringField;
      if (typeof field === 'string') {
        stringField = field;
        tempObj.display = stringField;
      }
      else {
        Object.assign(tempObj, field);
      }
      return tempObj;
    }).filter(tempObj => !!tempObj);


    this.displayValue = Observable.zip(
      Observable.from(displayValues as Array<any>)
        .concatMap(displayValue => {
          return this.getValue(displayValue)
            .map(val => {
              const returnObj = { ...displayValue};
              returnObj.display = val;
              if (
                  Array.isArray(returnObj.conditions)
                  && !returnObj.conditions.every(condition => ConditionalItem.from(returnObj.display, condition).verify())
              ) {
                return { display: ''} ;
              }
              if (returnObj.pattern) {
                returnObj.display = InterpolateService.interpolate(returnObj.pattern, returnObj.display, false, {
                  clearUnresolvedPatterns: true,
                  usePartialPath: false,
                  useEmptyValues: true
                });
              }
              delete returnObj.path;
              delete returnObj.pattern;
              delete returnObj.conditions;
              delete returnObj.source;
              return returnObj;
            });
        })
     ).combineAll()
      .map((val: any) => {
        switch (this.fieldTemplate) {
          case 'twolines':
          case 'additional-parenthesis':
            return { display: val.map(val => val.display).filter(val => !!val).join(', ')};
          default:
            return val;
        }

      });
  }

  private getValue(value) {
    if (!value) { return Observable.of(''); }
    let path = value.path || '.' + value.display;


    if (typeof value === 'string') {
      return Observable.of(value);
    } else if (value.source) {
      return this.getFromSource('item', '.' + value.display, value.fallback)
        .catch(() => '')
        .flatMap((itemValue) => {
          path = this.interpolatePath(path, itemValue);
          return this.getFromSource(value.source, path, value.fallback);
        });
    } else if (value.display) {
      path = this.interpolatePath(path, value.display);
      return this.getFromSource('item', path, value.fallback);
    } else {
      return Observable.of('');
    }
  }

  private interpolatePath(path, itemValue) {
    if (path) {
      const interpolateObject = Object.assign({ itemValue}, this.object);
      path = this.interpolate.interpolate(path, interpolateObject);
    }
    return path;
  }

  private getFromSource(source: string, path: string, fallback = '') {
    let sourceObject;
    switch (source.toLowerCase()) {
      case 'organization':
        sourceObject = Observable.of(this.orgService.organizations);
        break;
      case 'store':
        sourceObject = this.store.take(1).map(state => state);
        break;
      default:
        sourceObject = Observable.of(this.object);
        break;
    }

    return sourceObject.map(val => {
      try {
        return eval('val' + path);
      } catch (e) {
        return fallback;
      }
    });

  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
